import api from "@/api/v1-jwt/config/api";
import {
  useQuery,
  QueryFunctionContext,
  UseQueryOptions,
} from "@tanstack/react-query";
import { createErrorHandler } from "@/utils/errorHandler";
import sweepQueryKeys from "../query-keys";
import { SweepsEntity, sweepsEntityResponseSchema } from "../types";

type SweepSettingsEntityQueryKey = ReturnType<typeof sweepQueryKeys.entity>;

const fetchSweepSettingsEntity = async ({
  queryKey,
}: QueryFunctionContext<SweepSettingsEntityQueryKey>): Promise<SweepsEntity | null> => {
  const [, userId] = queryKey;
  const url = `/platform/sweeps/setting/${userId}`;
  const response = await api.get(url);
  try {
    const { data } = sweepsEntityResponseSchema.parse(response.data);
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const sweepSettingsEntity = {
  createOptions: <TSelected = SweepsEntity | null>(
    userId: string,
    options?: Omit<
      UseQueryOptions<
        SweepsEntity | null,
        Error,
        TSelected,
        SweepSettingsEntityQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const defaultOptions: UseQueryOptions<
      SweepsEntity | null,
      Error,
      TSelected,
      SweepSettingsEntityQueryKey
    > = {
      queryKey: sweepQueryKeys.entity(userId),
      queryFn: fetchSweepSettingsEntity,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: createErrorHandler(),
    };
    return { ...defaultOptions, ...options };
  },
  useQuery: <TSelected = SweepsEntity | null>(
    userId: string,
    options?: Omit<
      UseQueryOptions<
        SweepsEntity | null,
        Error,
        TSelected,
        SweepSettingsEntityQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const queryOptions = sweepSettingsEntity.createOptions<TSelected>(
      userId,
      options
    );
    return useQuery<
      SweepsEntity | null,
      Error,
      TSelected,
      SweepSettingsEntityQueryKey
    >({
      ...queryOptions,
      enabled: options?.enabled ?? !!userId,
    });
  },
};
